import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { CateringPackage } from '../../(routes)/online-ordering/[resturants]/catering/types';
import { CategoryItem } from '../../(routes)/online-ordering/types';
import { ServiceTypeV2 } from '@zeus/shared';
interface CateringModalTypes {
  display: boolean;
  cateringPackage?: CateringPackage | null;
}

export interface InquireDataState {
  headCount?: number;
  openCalendar?: boolean;
  openClock?: boolean;
  eventType?: string;
  venueDetail?: string;
  serviceType?: ServiceTypeV2;
  eventDate?: string; //'YYYY-MM-DD'
  eventTime?: string; //'HH:mm'
  email?: string;
  specialRequest?: string;
}
export interface CateringCartItemType {
  id: number;
  item: CategoryItem;
  quantity: number;
}
interface InitialStateType {
  selectedPackages: CateringPackage[];
  cateringModal: CateringModalTypes;
  inquireData: InquireDataState | null; // we will keep email on clearCateringCart
  catertingCartItems: CateringCartItemType[];
  cateringItemData: { totalPrice: number; totalQuantity: number };
  selectedCateringCategoryId: number;
  cateringMenuDataLoading: { isLoading: boolean; message: string };
}

const initialState: InitialStateType = {
  selectedPackages: [],
  cateringModal: { display: false, cateringPackage: null },
  inquireData: null,
  catertingCartItems: [],
  cateringItemData: { totalPrice: 0, totalQuantity: 0 },
  selectedCateringCategoryId: 1,
  cateringMenuDataLoading: { isLoading: false, message: '' },
};

export const cateringSlice = createSlice({
  name: 'catering',
  initialState,
  reducers: {
    addToCateringCart: (state, action: PayloadAction<CateringPackage>) => {
      if (state.inquireData) {
        state.inquireData.serviceType = action.payload.premisesType;
      }
      state.selectedPackages.push(action.payload);
    },
    addToCateringItemCart: (state, action: PayloadAction<CategoryItem>) => {
      const newItem = action.payload;
      // Check if item already exists
      const existingItem = state.catertingCartItems.find((ci) => ci.id === newItem.id);

      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        state.catertingCartItems.push({
          id: newItem.id,
          item: newItem,
          quantity: 1,
        });
      }
      state.cateringItemData.totalQuantity += 1;
      state.cateringItemData.totalPrice += newItem.itemPrice;
    },
    removeFromCateringItemCart: (state, action: PayloadAction<CategoryItem>) => {
      const itemToRemove = action.payload;

      const existingItem = state.catertingCartItems.find((ci) => ci.id === itemToRemove.id);

      if (!existingItem) return;

      // decrease quantity
      existingItem.quantity -= 1;

      // update totals
      state.cateringItemData.totalQuantity -= 1;
      state.cateringItemData.totalPrice -= itemToRemove.itemPrice;

      // remove item if quantity becomes 0
      if (existingItem.quantity === 0) {
        state.catertingCartItems = state.catertingCartItems.filter((ci) => ci.id !== itemToRemove.id);
      }
    },
    clearItemsCart: (state) => {
      state.cateringItemData.totalQuantity = 0;
      state.cateringItemData.totalPrice = 0;
      state.catertingCartItems = [];
    },
    updateCateringModal: (state, action: PayloadAction<CateringModalTypes>) => {
      state.cateringModal = action.payload;
    },
    updateInquiryData: (state, action: PayloadAction<InquireDataState>) => {
      state.inquireData = action.payload;
    },
    clearCateringCart: (state) => {
      const savedEmail = state.inquireData?.email;
      state.selectedPackages = [];
      state.cateringItemData = { totalPrice: 0, totalQuantity: 0 };
      state.catertingCartItems = [];
      state.inquireData = savedEmail ? { email: savedEmail } : null;
    },
    clearPackageCartBasedOnServicesType: (state, action: PayloadAction<ServiceTypeV2>) => {
      if (state.selectedPackages.length > 0 && action.payload != state.selectedPackages?.[0]?.premisesType)
        //since one package is allowed at the moment(requirement)
        state.selectedPackages = [];
    },
    setSelectedCateringCategory: (state, action: PayloadAction<number>) => {
      state.selectedCateringCategoryId = action.payload;
    },
    setCateringMenuDataLoading: (state, action: PayloadAction<InitialStateType['cateringMenuDataLoading']>) => {
      state.cateringMenuDataLoading = action.payload;
    },
    setSpecialRequest: (state, action: PayloadAction<string>) => {
      state.inquireData = { ...state.inquireData, specialRequest: action.payload };
    },
    removePackageById: (state, action: PayloadAction<number>) => {
      state.selectedPackages = state.selectedPackages.filter((pkg) => pkg.id !== action.payload);
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  addToCateringCart,
  updateCateringModal,
  updateInquiryData,
  clearCateringCart,
  addToCateringItemCart,
  removeFromCateringItemCart,
  setSelectedCateringCategory,
  setCateringMenuDataLoading,
  setSpecialRequest,
  clearPackageCartBasedOnServicesType,
  removePackageById,
  clearItemsCart,
} = cateringSlice.actions;

export default cateringSlice.reducer;
